import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { SimpleCartItem } from '@/types/cart.types';
import { MEMBER_SESSION_CLEARED_EVENT } from '@/utils/memberSession';

interface CartContextType {
  cart: SimpleCartItem[];
  addToCart: (productId: string) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  getCartQuantity: (productId: string) => number;
  getTotalCartItems: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = 'teebox_shop_cart';

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<SimpleCartItem[]>(() => {
    // Initialize from localStorage
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  });

  // A different member logged in, or this one signed out: the cart in memory
  // was read from storage before it was cleared and would be written back.
  useEffect(() => {
    const reset = () => setCart([]);
    window.addEventListener(MEMBER_SESSION_CLEARED_EVENT, reset);
    return () => window.removeEventListener(MEMBER_SESSION_CLEARED_EVENT, reset);
  }, []);

  // Persist to localStorage whenever cart changes
  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  }, [cart]);

  const addToCart = (productId: string) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.productId === productId);
      if (existingItem) {
        return prevCart.map(item =>
          item.productId === productId
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevCart, { productId, quantity: 1 }];
    });
  };

  const removeFromCart = (productId: string) => {
    setCart(prevCart => prevCart.filter(item => item.productId !== productId));
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity === 0) {
      removeFromCart(productId);
    } else {
      setCart(prevCart => {
        const existingItem = prevCart.find(item => item.productId === productId);
        if (existingItem) {
          return prevCart.map(item =>
            item.productId === productId
              ? { ...item, quantity }
              : item
          );
        }
        return [...prevCart, { productId, quantity }];
      });
    }
  };

  const clearCart = () => {
    setCart([]);
  };

  const getCartQuantity = (productId: string): number => {
    const item = cart.find(item => item.productId === productId);
    return item ? item.quantity : 0;
  };

  const getTotalCartItems = (): number => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartQuantity,
        getTotalCartItems,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
